import React, { useEffect, useMemo, useRef, useState } from 'react';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import { getDeepDiveInfo, getTimeline } from 'utils/apiRequests';
import {
	Header,
	LoadingContainer,
	NoData,
	SectionWrapper,
	Title,
} from '../../Charts/ChartList.styles';
import {
	ApLoaderDefault,
	ApLoaderText,
	ApSimpleDropdown,
	colors,
} from '@alixpartners/ui-components';
import { isUndefined } from 'lodash';
import { renderToString } from 'react-dom/server';
import {
	FilterWrapper,
	TooltipContainer,
	TooltipItemTitle,
	StatsWrapper,
	KPISection,
	TableWrapper,
	KpiCard,
	CardValue,
	CardText,
	ToggleSwitch,
} from './TimelineOne.styles';
import { formatNumber, formatSpendWithDecimalAndSymbol } from 'utils/number';
import { getDateFormat } from 'CategoryManagement/utils';
import moment from 'moment';
import { AgGridReact } from '@ag-grid-community/react';
import { NotificationTypes } from 'components/Toast/constants';
import { useToastMessageContext } from 'contexts/ToastMessageContext/ToastMessageProvider';
import { Loader } from 'SpeedToMarket/Tabs/CategoryWidget/utils';

const FONTSTYLE = {
	fontSize: '11px',
	fontWeight: '400',
	lineHeight: '13px',
	letterSpacing: 0,
	color: colors.grey7,
};

const getDistinctLevel3WithUnits = data => {
	return data.reduce((acc, item) => {
		if (!acc[item.level_3]) {
			acc[item.level_3] = item.units;
		}
		return acc;
	}, {});
};

const seriesType = {
	supplier: 'supplier',
	commodity: 'commodity',
};

//to scale volume to range 0 to 20
const scaleMarker = (value, min, max) => {
	return Math.round(((value - min) / (max - min)) * 20);
};

const TimelineOne = ({
	modelId,
	filterData,
	skuDetails,
	commodityHierarchy,
	currencySymbol,
	hideDecimals,
}) => {
	const { sku, totalSpend, supplierCount } = skuDetails;
	const { showToast } = useToastMessageContext();
	const chartRef = useRef();
	const [chartTitle, setChartTitle] = useState('');
	const [isLoading, setIsLoading] = useState(true);
	const [data, setData] = useState([]);
	const [filter, setFilter] = useState({
		label: 'PO Wt.Avg Unit Price',
		value: 'avG_UNIT_PRICE_USD',
	});
	const [options, setOptions] = useState({
		chart: {
			type: 'line',
			zoomType: 'x',
		},
		exporting: { enabled: false, allowHTML: true },
		credits: { enabled: false },
		title: {
			text: '',
			style: {
				...FONTSTYLE,
				textOverflow: 'ellipsis',
			},
		},
		xAxis: {
			type: 'datetime',
			gridLineWidth: 0,
			lineColor: colors.black,
			title: {
				text: 'Date',
				style: {
					...FONTSTYLE,
				},
			},
		},
		yAxis: [
			{
				gridLineWidth: 0,
				lineColor: colors.black,
				lineWidth: 1,
				labels: {
					formatter: function() {
						if (isUndefined(this.value)) {
							return '';
						} else {
							return formatSpendWithDecimalAndSymbol(
								this.value,
								currencySymbol,
								hideDecimals,
								0
							);
						}
					},
				},
				title: {
					text: filter.label,
					style: {
						...FONTSTYLE,
						textOverflow: 'ellipsis',
					},
				},
			},
		],
		series: [],
		tooltip: {
			shared: false,
			useHTML: true,
			backgroundColor: 'rgba(255,255,255, 0.9)',
			borderWidth: 0,
			borderRadius: 2,
			outside: true,
			formatter: function() {
				const { x, y, series, point } = this;
				const dateObj = new Date(x);
				const date = dateObj.toISOString();
				if (point.additionalInfo.seriesType === seriesType.supplier) {
					return renderToString(
						<TooltipContainer>
							<TooltipItemTitle>{series.name}</TooltipItemTitle>
							<div id="tooltip_content">
								<div>Date - {moment(date).format('MMMM YYYY')}</div>
								<div>
									{point?.additionalInfo?.seriesFor} -
									{formatSpendWithDecimalAndSymbol(
										y,
										currencySymbol,
										hideDecimals,
										1
									).toUpperCase()}
								</div>
								<div>
									PO Volume - {formatNumber(point?.additionalInfo?.volume)}
								</div>
							</div>
						</TooltipContainer>
					);
				}
				if (point.additionalInfo.seriesType === seriesType.commodity) {
					return renderToString(
						<TooltipContainer>
							<TooltipItemTitle>{series.name}</TooltipItemTitle>
							<div
								style={{
									display: 'flex',
									flexDirection: 'column',
								}}
							>
								<div>Date - {moment(date).format('MMMM YYYY')}</div>
								<div>Average - {`${y.toFixed(2)}`}</div>
							</div>
						</TooltipContainer>
					);
				}
			},
		},
		legend: {
			itemStyle: {
				textTransform: 'uppercase',
			},
		},
	});
	const [commodityL2Options] = useState(
		Object.keys(commodityHierarchy).map(x => {
			return { label: x, value: x };
		})
	);
	const [commodityL2Selected, setCommodityL2Selected] = useState({});
	const [commodityL3Options, setCommodityL3Options] = useState([]);
	const [commodityL3Selected, setCommodityL3Selected] = useState({});
	const [commodityUnits, setCommodityUnits] = useState({});
	const [isSuppliersLoading, setIsSuppliersLoading] = useState(true);
	const [supplierData, setSupplierData] = useState([]);
	const [isScaledVolume, setIsScaledVolume] = useState(false);
	const gridStyle = useMemo(() => ({ height: '300px' }), []);
	const defaultColDef = useMemo(() => {
		return {
			flex: 1,
			minWidth: 100,
		};
	}, []);
	const columnDefs = [
		{ headerName: 'Supplier', field: 'supplier' },
		{
			headerName: 'Total Period Spend',
			field: 'totalSpend',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},

		{
			headerName: 'Supplier Wt.Avg Price',
			field: 'avgUnitPrice',
			valueFormatter: params => {
				if (isUndefined(params.value)) {
					return '';
				} else {
					return formatSpendWithDecimalAndSymbol(
						params.value,
						currencySymbol,
						hideDecimals,
						1
					).toUpperCase();
				}
			},
		},
	];

	useEffect(
		() => {
			if (sku !== '') {
				if (data.length === 0) {
					const { dateFrom, dateTo } = getDateFormat(
						filterData.dateRange.stateRange
					);
					const payload = {
						filtersDto: {
							sku: [sku],
							category1: filterData.category_1.selectedOption,
							category2: filterData.category_2.selectedOption,
							category3: filterData.category_3.selectedOption,
							commodityCode: filterData.commodity_Code.selectedOption,
							commodityFamily: filterData.commodity_Family.selectedOption,
							originCountry: filterData.origin_Country.selectedOption,
						},
						modelId: modelId,
						startDate: dateFrom,
						endDate: dateTo,
					};
					getTimeline(payload).then(timelineResponse => {
						if (timelineResponse.succeeded) {
							if (Object.keys(timelineResponse.data).length > 0) {
								const { timeLineData, commodityData } = timelineResponse.data;
								if (timeLineData.length > 0 || commodityData.length > 0) {
									const supplierSeries = getSeriesData(
										timeLineData,
										'supplier',
										filter.value,
										0
									);
									setOptions(prevState => ({
										...prevState,
										series: supplierSeries,
									}));
									setData(timelineResponse.data);
									if (commodityData !== null) {
										if (commodityData.length !== 0) {
											const unitsResult = getDistinctLevel3WithUnits(
												commodityData
											);
											setCommodityUnits(unitsResult);
										}
									}

									const distinctSKUDescription = [
										...new Set(timeLineData.map(x => x.skU_Description)),
									];
									const filteredSKUDescription = distinctSKUDescription.filter(
										x => x !== null
									);
									setChartTitle(
										`SKU Description - ${filteredSKUDescription[0]}`
									);
									setIsLoading(false);
								}
							}
						} else {
							setIsLoading(false);
							showToast(
								'Something went wrong!',
								NotificationTypes.danger,
								2500
							);
						}
					});
				} else {
					let newCommditySeriesData = [],
						newsupplierSeriesData = [];
					if (Object.keys(filter).length !== 0) {
						if (data.timeLineData.length > 0) {
							newsupplierSeriesData = getSeriesData(
								data.timeLineData,
								'supplier',
								filter.value,
								0
							);
						} else {
							showToast('Data not available.', NotificationTypes.warning, 2500);
						}
					}
					if (Object.keys(commodityL3Selected).length !== 0) {
						if (data.commodityData.length > 0) {
							newCommditySeriesData = getCommoditySeriesData(
								data.commodityData,
								commodityL3Selected.value
							);
						} else {
							showToast('Data not available.', NotificationTypes.warning, 2500);
						}
					}
					setOptions(prevState => ({
						...prevState,
						series: [...newsupplierSeriesData, ...newCommditySeriesData],
						yAxis: getYAxisOptions(
							Object.keys(commodityL3Selected).length !== 0,
							commodityL3Selected.label
						),
					}));
				}
			}
		}, // eslint-disable-next-line
		[filter, commodityL3Selected, isScaledVolume]
	);

	useEffect(
		() => {
			if (sku !== '') {
				const { dateFrom, dateTo } = getDateFormat(
					filterData.dateRange.stateRange
				);
				const payload = {
					filtersDto: {
						sku: [sku],
						category1: filterData.category_1.selectedOption,
						category2: filterData.category_2.selectedOption,
						category3: filterData.category_3.selectedOption,
						commodityCode: filterData.commodity_Code.selectedOption,
						commodityFamily: filterData.commodity_Family.selectedOption,
						originCountry: filterData.origin_Country.selectedOption,
					},
					modelId: modelId,
					startDate: dateFrom,
					endDate: dateTo,
				};
				getDeepDiveInfo(payload).then(result => {
					if (result.succeeded) {
						setSupplierData(result.data);
						setIsSuppliersLoading(false);
					}
				});
			}
		}, // eslint-disable-next-line
		[]
	);

	const getSeriesData = (data, key, filterValue, axisNo) => {
		const newData = [...new Set(data.map(item => item[key]))];
		const volumes = data.map(item => item.volume);
		const minVolume = Math.min(...volumes);
		const maxVolume = Math.max(...volumes);
		return newData.map(el => {
			return {
				name: el,
				data: data
					.filter(item => item[key] === el)
					.map(item => {
						return {
							x: Date.UTC(item.year, item.month - 1),
							y: Number(item[filterValue]),
							marker: {
								radius: isScaledVolume
									? scaleMarker(item.volume, minVolume, maxVolume)
									: 5,
								symbol: 'circle',
							},
							additionalInfo: {
								volume: item.volume,
								seriesType: seriesType.supplier,
								seriesFor: filter.label,
							},
						};
					})
					.sort((a, b) => a.x - b.x),
				yAxis: axisNo,
			};
		});
	};

	const onCommodityL2ChangeHandler = object => {
		setCommodityL2Selected(object);
		const newL3Options = commodityHierarchy[object.value].map(x => {
			return { label: x, value: x };
		});
		setCommodityL3Options(newL3Options);
		setCommodityL3Selected({});
		setOptions(prevState => ({
			...prevState,
			series: [...prevState.series.filter(x => x.yAxis === 0)],
			yAxis: getYAxisOptions(false, ''),
		}));
	};

	const getCommoditySeriesData = (data, value) => {
		const result = data.filter(x => x.level_3 === value);
		const newData = [...new Set(result.map(item => item['level_3']))];
		return newData.map(el => {
			return {
				name: el,
				data: result
					.filter(item => item['level_3'] === el)
					.map(item => {
						return {
							x: Date.UTC(item.year, item.month - 1),
							y: Number(item['average']),
							marker: {
								radius: 5,
								symbol: 'circle',
							},
							additionalInfo: {
								seriesType: seriesType.commodity,
							},
						};
					})
					.sort((a, b) => a.x - b.x),
				yAxis: 1,
			};
		});
	};

	const getYAxisOptions = (isCommoditySelected, label) => {
		if (isCommoditySelected) {
			return [
				{
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
					labels: {
						formatter: function() {
							if (isUndefined(this.value)) {
								return '';
							} else {
								return formatSpendWithDecimalAndSymbol(
									this.value,
									currencySymbol,
									hideDecimals,
									0
								);
							}
						},
					},
					title: {
						text: filter.label,
						style: {
							...FONTSTYLE,
							textOverflow: 'ellipsis',
						},
					},
				},
				{
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
					labels: {
						formatter: function() {
							if (isUndefined(this.value)) {
								return '';
							} else {
								return formatNumber(this.value);
							}
						},
					},
					title: {
						text: `Commodity (${commodityUnits[label]})`,
						style: {
							...FONTSTYLE,
							textOverflow: 'ellipsis',
						},
					},
					opposite: true,
				},
			];
		} else {
			return [
				{
					gridLineWidth: 0,
					lineColor: colors.black,
					lineWidth: 1,
					labels: {
						formatter: function() {
							if (isUndefined(this.value)) {
								return '';
							} else {
								return formatSpendWithDecimalAndSymbol(
									this.value,
									currencySymbol,
									hideDecimals,
									0
								);
							}
						},
					},
					title: {
						text: filter.label,
						style: {
							...FONTSTYLE,
							textOverflow: 'ellipsis',
						},
					},
				},
			];
		}
	};

	const KpiElement = ({ cardTitle, value }) => {
		return (
			<KpiCard>
				<CardValue>{value}</CardValue>
				<CardText>{cardTitle}</CardText>
			</KpiCard>
		);
	};

	return (
		<SectionWrapper>
			<Header>
				<Title>{chartTitle}</Title>
				<div
					style={{
						display: 'flex',
						justifyContent: 'flex-end',
						fontStyle: 'italic',
						color: '#737373',
					}}
				>
					**Commodity data source: MINTEC
				</div>
			</Header>
			{isLoading ? (
				<LoadingContainer>
					<ApLoaderDefault loaderText={() => <ApLoaderText />} />
				</LoadingContainer>
			) : (
				<>
					{data.length === 0 ? (
						<NoData>No data available</NoData>
					) : (
						<>
							<FilterWrapper>
								<div id="price_filter">
									<ApSimpleDropdown
										onChange={e => setFilter(e)}
										options={[
											{
												label: 'PO Wt.Avg Unit Price',
												value: 'avG_UNIT_PRICE_USD',
											},
											{ label: 'LPP (Last Price Paid)', value: 'lpp' },
										]}
										selected={filter}
										label="Select Option"
									/>
								</div>
								<div id="commodity_l2_filter">
									<ApSimpleDropdown
										onChange={onCommodityL2ChangeHandler}
										options={commodityL2Options}
										selected={commodityL2Selected}
										label="Select L2 Commodity"
									/>
								</div>
								<div id="commodity_l3_filter">
									<ApSimpleDropdown
										onChange={v => setCommodityL3Selected(v)}
										options={commodityL3Options}
										selected={commodityL3Selected}
										label="Select L3 Commodity"
										disabled={!Object.keys(commodityL2Selected).length > 0}
									/>
								</div>
								<div id="scale_marker_by_volume">
									<div>Volume Scale Marker:</div>
									<ToggleSwitch
										defaultChecked={isScaledVolume}
										onChange={() => setIsScaledVolume(!isScaledVolume)}
									/>
								</div>
							</FilterWrapper>
							<HighchartsReact
								highcharts={Highcharts}
								options={options}
								ref={chartRef}
							/>
						</>
					)}
				</>
			)}
			<StatsWrapper>
				{isSuppliersLoading ? (
					<Loader />
				) : (
					<>
						<KPISection>
							<KpiElement cardTitle="SKU ID Number" value={sku} />
							<KpiElement
								cardTitle="Number of Suppliers"
								value={supplierCount}
							/>
							<KpiElement
								cardTitle="Purchase Amount"
								value={formatSpendWithDecimalAndSymbol(
									totalSpend,
									currencySymbol,
									hideDecimals,
									1
								).toUpperCase()}
							/>
						</KPISection>
						<TableWrapper>
							<div style={gridStyle} className="ag-theme-balham">
								<AgGridReact
									rowData={supplierData}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									pagination={true}
									paginationPageSize={20}
								/>
							</div>
						</TableWrapper>
					</>
				)}
			</StatsWrapper>
		</SectionWrapper>
	);
};

export default TimelineOne;
